import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Navbar,
  Nav,
  NavDropdown,
  Form,
  Container,
} from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AddNewEnvironmentPopup from "../../containers/EnvironmentDetails/AddNewEnvironmentPopup";
import WadeLogo_White from "../../components/Icons/WadeLogo_White.svg";
import addPlusicon from "../../components/Icons/addPlusicon.svg";
import Group543 from "../../assets/images/svg/group543";
import HelpDropdown from "../Help/help";
import "./NavHeader.scss";
import { useCustomer } from "../../Contexts/CustomerContext";
import { useEnvironment } from "../../Contexts/EnvironmentContext";

interface NavHeaderProps {
  updateEnvironmentsDetails: (environmentId?: number) => void;
  isLoggedIn: boolean;
}

const NavHeader: React.FC<NavHeaderProps> = ({
  updateEnvironmentsDetails,
  isLoggedIn,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const updateEnvironmentRef = useRef(updateEnvironmentsDetails);
  const [showAddEnvPopup, setShowAddEnvPopup] = useState(false);

  const {
    customers,
    activeCustomer,
    hasMultipleCustomers,
    isLoading: isCustomerLoading,
    isSwitching: isCustomerSwitching,
    selectCustomer,
  } = useCustomer();

  const {
    environments,
    activeEnvironment,
    activeEnvironmentId,
    isLoading: isEnvironmentLoading,
    isSwitching: isEnvironmentSwitching,
    selectEnvironment,
    refreshEnvironments,
  } = useEnvironment();

  useEffect(() => {
    updateEnvironmentRef.current = updateEnvironmentsDetails;
  }, [updateEnvironmentsDetails]);

  useEffect(() => {
    updateEnvironmentRef.current(activeEnvironmentId);
  }, [activeEnvironmentId]);

  const shouldRenderHeader = useMemo(
    () => isLoggedIn || localStorage.getItem("isLoggedIn") === "true",
    [isLoggedIn]
  );

  const handleCustomerSelection = (customerId: number) => {
    selectCustomer(customerId)
      .then(() => {
        navigate("/workspaceDetails");
      })
      .catch((error) => {
        console.error("Customer switch failed", error);
      });
  };

  const handleEnvironmentSelection = (environmentId: number) => {
    selectEnvironment(environmentId)
      .then(() => {
        updateEnvironmentsDetails(environmentId);
        navigate("/workspaceDetails");
      })
      .catch((error) => {
        console.error("Environment switch failed", error);
      });
  };

  const customerDropdownTitle =
    activeCustomer?.customer_name ??
    (isCustomerLoading ? "Loading customers..." : "Select customer");

  const environmentDropdownTitle =
    activeEnvironment?.customer_environment_name ??
    (isEnvironmentLoading ? "Loading workspaces..." : "Select workspace");

  const isCustomerDropdownDisabled = isCustomerLoading || isCustomerSwitching;
  const isEnvironmentDropdownDisabled =
    isEnvironmentLoading || isEnvironmentSwitching || !environments.length;

  const onCloseEnvironmentPopup = () => {
    setShowAddEnvPopup(false);
    refreshEnvironments();
  };

  if (!shouldRenderHeader) {
    return null;
  }

  return (
    <div className="navbar-container">
      <Navbar className="nav-header-outer-container">
        <Container fluid className="custom-header-fluid">
          <Navbar.Brand className="custom-brand">
            <img
              id="wade-logo-white-header"
              src={WadeLogo_White}
              alt="WadeLogo_White"
              height={"28px"}
            />
          </Navbar.Brand>
          <Form className="environment_inner_container">
            <HelpDropdown />
            {hasMultipleCustomers && (
              <div className="navbar-environment">
                <NavDropdown
                  id="customer-nav-dropdown"
                  title={customerDropdownTitle}
                  renderMenuOnMount
                  disabled={isCustomerDropdownDisabled}
                >
                  {customers.map((customer) => (
                    <NavDropdown.Item
                      key={customer.customer_id}
                      active={customer.customer_id === activeCustomer?.customer_id}
                      onClick={() => handleCustomerSelection(customer.customer_id)}
                    >
                      {customer.customer_name}
                    </NavDropdown.Item>
                  ))}
                </NavDropdown>
              </div>
            )}
            {location.pathname !== "/support" && (
              <div className="navbar-environment">
                <NavDropdown
                  title={environmentDropdownTitle}
                  id="environment-nav-dropdown"
                  renderMenuOnMount
                  disabled={isEnvironmentDropdownDisabled}
                >
                  <NavDropdown.Item
                    className="nav-dropdown-add-workspace"
                    onClick={() => setShowAddEnvPopup(true)}
                  >
                    <img alt="add workspace" src={addPlusicon} />
                    <span>Add Workspace</span>
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  {environments.map((environmentOption) => (
                    <NavDropdown.Item
                      key={environmentOption.environment_id}
                      active={environmentOption.environment_id === activeEnvironmentId}
                      onClick={() =>
                        handleEnvironmentSelection(environmentOption.environment_id)
                      }
                      className="nav-dropdown-environment-item"
                    >
                      {environmentOption.customer_environment_name}
                    </NavDropdown.Item>
                  ))}
                </NavDropdown>
              </div>
            )}
            <NavDropdown
              id="user-nav-dropdown"
              title={
                <div className="navbar-profile">
                  <div className="navbar-email">
                    {localStorage.getItem("email")}
                  </div>
                  <Group543 />
                </div>
              }
            >
              <Nav.Link
                id="basic-nav-dropdown-link_profileIcon"
                href="/support"
                as={Link}
                to="/support"
                active={location.pathname === "/support"}
              >
                Support
              </Nav.Link>
            </NavDropdown>
          </Form>
        </Container>
      </Navbar>
      {showAddEnvPopup && (
        <AddNewEnvironmentPopup
          show={showAddEnvPopup}
          onHide={onCloseEnvironmentPopup}
        />
      )}
    </div>
  );
};

export default NavHeader;
